import apiClient from './apiClient';

export interface WorkoutListItem {
  id: string;
  name: string;
  sportIdentifier: string;
  sportLabel: string | null;
  themeLabel: string;
  createdAt: string;
  isStructured: boolean;
  isDraft: boolean;
  isTest: boolean;
}

export interface WorkoutList {
  items: WorkoutListItem[];
  total: number;
}

export interface WorkoutKpis {
  total: number;
  structured: number;
  drafts: number;
}

/** Swim leaves: `(…)` equipment + free-text cue (PRD 03b FR12). Other sports carry a cue only. */
export interface IntervalNote {
  equipment: string[];
  cue: string | null;
}

export interface WorkoutInterval {
  id: string;
  section: string | null;
  orderIndex: number;
  repeatCount: number;
  durationSec: number | null;
  distanceM: number | null;
  reps: number | null;
  unilateral: boolean;
  isMax: boolean;
  /** PRD 15 — a test step with no intensity. */
  allOut: boolean;
  intensityPctIcMin: number | null;
  intensityPctIcMax: number | null;
  zoneLabel: string | null;
  cadence: number | null;
  rpe: number | null;
  note: IntervalNote | null;
  children: WorkoutInterval[];
}

export interface RenderedSection {
  section: 'WARMUP' | 'MAIN' | 'COOLDOWN';
  source: 'stored' | 'theme';
  intervals: WorkoutInterval[];
}

export interface RenderedWorkout {
  sections: RenderedSection[];
  durationSec: number | null;
  distanceM: number | null;
}

export interface WorkoutDetail {
  id: string;
  name: string;
  sportIdentifier: string;
  sportLabel: string | null;
  themeId: string;
  themeLabel: string;
  isStructured: boolean;
  isDraft: boolean;
  contentText: string | null;
  durationSec: number | null;
  distanceM: number | null;
  sourcePattern: string | null;
  note: string | null;
  warmupText: string | null;
  cooldownText: string | null;
  warmupMode: 'AUTO' | 'MANUAL';
  cooldownMode: 'IN_BODY' | 'AUTO' | 'MANUAL';
  coachNoteOverride: string | null;
  nutritionOverride: string | null;
  coachNote: string | null;
  nutrition: string | null;
  isTest: boolean;
  createdAt: string;
  intervals: WorkoutInterval[];
  rendered: RenderedWorkout;
  /** Set only when a scaling row was applied. */
  renderedFormat: string | null;
}

export interface DecodedInterval {
  section: string | null;
  repeatCount: number;
  durationSec: number | null;
  distanceM: number | null;
  reps: number | null;
  unilateral: boolean;
  isMax: boolean;
  intensityPctIcMin: number | null;
  intensityPctIcMax: number | null;
  /** PRD 10 §5.1 — `PCT` when the step carries a percentage band, `ALL_OUT` for a test step (PRD 15), `ZONE` otherwise. */
  mode: 'ZONE' | 'PCT' | 'ALL_OUT';
  /** Parsed from the pattern but never stored; the unit is read from the workout's sport. */
  intensityUnit: string | null;
  zoneLabel: string | null;
  cadence: number | null;
  note: IntervalNote | null;
  isRecovery: boolean;
  children: DecodedInterval[];
}

export interface DecodePreview {
  sections: DecodedInterval[];
  estimatedDurationSec: number;
  distanceM: number | null;
  warnings: string[];
  matchedThemeId: string | null;
}

export interface WorkoutPayload {
  sportIdentifier: string;
  themeId: string;
  name?: string | null;
  sourcePattern?: string | null;
  contentText?: string | null;
  warmupText?: string | null;
  cooldownText?: string | null;
  warmupMode?: 'AUTO' | 'MANUAL';
  cooldownMode?: 'IN_BODY' | 'AUTO' | 'MANUAL';
  note?: string | null;
  coachNoteOverride?: string | null;
  nutritionOverride?: string | null;
  isTest?: boolean;
}

export interface DeleteImpact {
  plans: { id: string; name: string }[];
  cycles: { id: string; name: string }[];
  athletePlans: { id: string; name: string }[];
}

export interface WorkoutFilters {
  search?: string;
  themeIds?: string[];
  sportIds?: string[];
  testsOnly?: boolean;
  page?: number;
  pageSize?: number;
}

export const getWorkouts = async (filters: WorkoutFilters): Promise<WorkoutList> => {
  const params: Record<string, string | number> = {};
  if (filters.search) params.search = filters.search;
  if (filters.themeIds && filters.themeIds.length > 0) params.themeIds = filters.themeIds.join(',');
  if (filters.sportIds && filters.sportIds.length > 0) params.sportIds = filters.sportIds.join(',');
  if (filters.testsOnly) params.testsOnly = 'true';
  if (filters.page) params.page = filters.page;
  if (filters.pageSize) params.pageSize = filters.pageSize;
  const response = await apiClient.get('admin/workouts', { params });
  return response.data as WorkoutList;
};

export const getWorkoutKpis = async (): Promise<WorkoutKpis> => {
  const response = await apiClient.get('admin/workouts/kpis');
  return response.data as WorkoutKpis;
};

export const getWorkout = async (id: string, format?: string | null): Promise<WorkoutDetail> => {
  const response = await apiClient.get(`admin/workouts/${id}`, { params: format ? { format } : undefined });
  return response.data as WorkoutDetail;
};

export const decodeWorkout = async (sourcePattern: string, sportIdentifier: string): Promise<DecodePreview> => {
  const response = await apiClient.post('admin/workouts/decode', { sourcePattern, sportIdentifier });
  return response.data as DecodePreview;
};

export const createWorkout = async (payload: WorkoutPayload): Promise<WorkoutDetail> => {
  const response = await apiClient.post('admin/workouts', payload);
  return response.data as WorkoutDetail;
};

export const updateWorkout = async (id: string, payload: WorkoutPayload): Promise<WorkoutDetail> => {
  const response = await apiClient.put(`admin/workouts/${id}`, payload);
  return response.data as WorkoutDetail;
};

export const deleteWorkout = async (id: string): Promise<void> => {
  await apiClient.delete(`admin/workouts/${id}`);
};

export type ImportDecision = 'import' | 'draft' | 'exclude';
export type ImportStatus = 'ok' | 'needs_theme' | 'undecoded' | 'error';

export interface WorkoutImportRow {
  sport: string;
  name?: string | null;
  pattern: string;
  theme?: string | null;
  themeId?: string | null;
  note?: string | null;
  coachNote?: string | null;
  decision?: ImportDecision;
}

export interface WorkoutImportReportRow {
  index: number;
  name: string;
  sport: string;
  sportLabel: string | null;
  status: ImportStatus;
  themeId: string | null;
  themeLabel: string | null;
  intervalsCount: number | null;
  detail: string;
  duplicate: boolean;
}

export interface WorkoutImportReport {
  dryRun: boolean;
  summary: {
    total: number;
    decoded: number;
    needsTheme: number;
    undecoded: number;
    errors: number;
    imported: number;
    drafted: number;
    excluded: number;
  };
  rows: WorkoutImportReportRow[];
}

export const importWorkouts = async (rows: WorkoutImportRow[], dryRun: boolean): Promise<WorkoutImportReport> => {
  const response = await apiClient.post('admin/workouts/import', { rows, dryRun });
  return response.data as WorkoutImportReport;
};
